import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import {
  getPettyCashSummary,
  listAprDocuments,
  listContracts,
  listInstitutionalLoans,
  listPettyCashMovements,
  type PettyCashMovementItem,
} from "@/lib/api";

export default function AdministracionPage() {
  const navigate = useNavigate();
  const { session } = useSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    contratosActivos: 0,
    saldoCaja: 0,
    prestamosVigentes: 0,
    documentosTotales: 0,
  });
  const [movimientos, setMovimientos] = useState<PettyCashMovementItem[]>([]);

  useEffect(() => {
    async function loadData(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");

      try {
        const [contratos, resumenCaja, prestamos, documentos, movimientosCaja] = await Promise.all([
          listContracts(session),
          getPettyCashSummary(session),
          listInstitutionalLoans(session),
          listAprDocuments(session),
          listPettyCashMovements(session, { limit: 6 }),
        ]);

        setStats({
          contratosActivos: contratos.filter((contrato) => contrato.status === "activo").length,
          saldoCaja: resumenCaja.saldoActual,
          prestamosVigentes: prestamos.filter((prestamo) => prestamo.status === "vigente").length,
          documentosTotales: documentos.length,
        });
        setMovimientos(movimientosCaja);
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo cargar el módulo de administración");
        setMovimientos([]);
      } finally {
        setLoading(false);
      }
    }

    void loadData();
  }, [session]);

  const modulos = useMemo(
    () => [
      {
        label: "Contratos",
        icon: "ri-file-text-line",
        desc: "Gestión de contratos de servicio",
        color: "text-blue-600",
        bg: "bg-blue-50",
        count: `${stats.contratosActivos.toLocaleString("es-CL")} activos`,
        onClick: () => navigate("/contratos"),
      },
      {
        label: "Caja Chica",
        icon: "ri-safe-line",
        desc: "Control de ingresos y egresos",
        color: "text-emerald-600",
        bg: "bg-emerald-50",
        count: `${formatCLP(stats.saldoCaja)} saldo`,
        onClick: () => navigate("/caja-chica"),
      },
      {
        label: "Préstamos",
        icon: "ri-bank-line",
        desc: "Registro de préstamos y cuotas",
        color: "text-violet-600",
        bg: "bg-violet-50",
        count: `${stats.prestamosVigentes.toLocaleString("es-CL")} vigentes`,
        onClick: () => navigate("/prestamos"),
      },
      {
        label: "Documentación APR",
        icon: "ri-folder-line",
        desc: "Archivos y documentos oficiales",
        color: "text-amber-600",
        bg: "bg-amber-50",
        count: `${stats.documentosTotales.toLocaleString("es-CL")} documentos`,
        onClick: () => navigate("/documentacion-apr"),
      },
      {
        label: "Cortes de Servicio",
        icon: "ri-scissors-line",
        desc: "Generación y gestión de cortes por morosidad",
        color: "text-red-600",
        bg: "bg-red-50",
        count: "Gestionar cortes",
        onClick: () => navigate("/cortes-servicio"),
      },
    ],
    [navigate, stats],
  );

  const movimientosRecientes = useMemo(
    () => movimientos.slice().sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime()),
    [movimientos],
  );

  function formatCLP(value: number): string {
    return value.toLocaleString("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 });
  }

  function formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("es-CL");
  }

  function resolveMovementIcon(movimiento: PettyCashMovementItem): string {
    if (movimiento.movementType === "ingreso") return "ri-arrow-down-line";
    if (movimiento.category.toLowerCase().includes("mantenimiento")) return "ri-settings-3-line";
    if (movimiento.category.toLowerCase().includes("material")) return "ri-tools-line";
    if (movimiento.category.toLowerCase().includes("combustible")) return "ri-gas-station-line";
    return "ri-arrow-up-line";
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Administración</h1>
        <p className="text-sm text-gray-500 mt-0.5">Contratos, caja chica, préstamos y documentación</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {modulos.map((m) => (
          <div
            key={m.label}
            onClick={m.onClick}
            className={`bg-white rounded-xl border border-gray-100 p-6 hover:border-gray-200 hover:-translate-y-0.5 transition-all duration-200 ${m.onClick ? "cursor-pointer" : "cursor-default"}`}
          >
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${m.bg} mb-4`}>
              <i className={`${m.icon} text-2xl ${m.color}`} />
            </div>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">{m.label}</h3>
                <p className="text-sm text-gray-500 mb-3">{m.desc}</p>
              </div>
              {m.onClick && <i className="ri-arrow-right-line text-gray-300 mt-0.5" />}
            </div>
            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">{m.count}</span>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Movimientos de Caja Recientes</h2>
        <div className="space-y-3">
          {loading && <div className="text-sm text-gray-500">Cargando movimientos...</div>}
          {!loading && movimientosRecientes.length === 0 && (
            <div className="text-sm text-gray-500">No hay movimientos recientes.</div>
          )}
          {!loading &&
            movimientosRecientes.map((mov) => (
              <div key={mov.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${mov.movementType === "ingreso" ? "bg-emerald-100" : "bg-red-100"}`}>
                    <i
                      className={`text-sm ${resolveMovementIcon(mov)} ${mov.movementType === "ingreso" ? "text-emerald-600" : "text-red-600"}`}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">{mov.description}</div>
                    <div className="text-xs text-gray-400">{formatDate(mov.movementDate)}</div>
                  </div>
                </div>
                <span className={`text-sm font-semibold ${mov.movementType === "ingreso" ? "text-emerald-600" : "text-red-600"}`}>
                  {mov.movementType === "ingreso" ? "+" : "-"}
                  {formatCLP(mov.amount)}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
